import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { CapacitorHttp } from '@capacitor/core';
import { isNative } from './platform';

export interface Manifest {
  version: string;        // bundle version, e.g. "1.0.0-3" (appVersion-bundleVersion)
  url: string;            // absolute https URL to the signed bundle zip
  checksum: string;       // signed checksum from `@capgo/cli bundle encrypt`
  sessionKey: string;     // ivSessionKey from `@capgo/cli bundle encrypt`
  minNativeVersion: string; // min installed binary appVersion required, e.g. "1.0.0"
  channel: string;        // "production" for now
}

/**
 * -1 | 0 | 1 comparing dotted version segments. A numeric bundle suffix (the
 * `-N` in `1.0.0-N`) is significant and orders newer-after-older; a non-numeric
 * pre-release label (e.g. `-alpha`) is ignored.
 */
export function compareSemver(a: string, b: string): number {
  const parse = (v: string) => {
    const [base, suffix] = v.split('-');
    const parts = base.split('.').map((n) => parseInt(n, 10) || 0);
    const suffixNum = suffix !== undefined ? parseInt(suffix, 10) : NaN;
    if (!isNaN(suffixNum)) parts.push(suffixNum);
    return parts;
  };
  const [pa, pb] = [parse(a), parse(b)];
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d > 0 ? 1 : -1;
  }
  return 0;
}

const FAILED_KEY = 'trf.ota.failedBundleVersions';

/** Versions the plugin rolled back from, persisted across launches. Never throws. */
export function readFailedVersions(): string[] {
  try {
    const raw = localStorage.getItem(FAILED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function rememberFailedVersion(version: string): void {
  try {
    const all = readFailedVersions();
    if (!all.includes(version)) localStorage.setItem(FAILED_KEY, JSON.stringify([...all, version]));
  } catch (err) {
    console.warn('[OTA] could not persist failed bundle version:', err);
  }
}

/**
 * Apply only if the manifest bundle is newer than the installed bundle, has not
 * already failed on this device, AND the installed native binary is new enough
 * to run it (the compatibility gate).
 * @param currentBundleVersion plugin's CapacitorUpdater.current().bundle.version
 * @param nativeVersion        plugin's CapacitorUpdater.current().native (binary appVersion)
 * @param failedVersions       bundle versions the plugin already rolled back from
 */
export function shouldApplyBundle(
  m: Manifest,
  currentBundleVersion: string,
  nativeVersion: string,
  failedVersions: readonly string[] = [],
): boolean {
  if (compareSemver(m.minNativeVersion, nativeVersion) > 0) return false; // needs newer binary
  if (failedVersions.includes(m.version)) return false; // rolled back before: do not retry
  return compareSemver(m.version, currentBundleVersion) > 0;
}

const MANIFEST_URL = 'https://trfimagine.org/ota/manifest.json';

/**
 * Native-only. Fetches the OTA manifest, and if a newer + compatible signed
 * bundle exists, downloads it (signature verified against the embedded
 * publicKey) and activates it via set(), which switches to the bundle and
 * reloads the WebView immediately (the JS context is destroyed). A bundle the
 * plugin rolled back from (it never called notifyAppReady) is remembered and
 * not downloaded again. Never throws to the caller: any failure leaves the current bundle untouched.
 */
export async function checkForBundleUpdate(): Promise<void> {
  if (!isNative()) return;
  try {
    // Use the native HTTP client, NOT the WebView fetch(): a Capacitor app runs
    // on the https://localhost (Android) / capacitor://localhost (iOS) origin,
    // and the OTA host serves the manifest cross-origin with no CORS headers, so
    // a plain fetch() is blocked by the browser's CORS policy. CapacitorHttp goes
    // through the native layer and is not subject to CORS. (Verified on-device:
    // fetch() failed with "blocked by CORS policy" before this change.)
    const res = await CapacitorHttp.get({ url: MANIFEST_URL, headers: { 'Cache-Control': 'no-store' } });
    if (res.status < 200 || res.status >= 300) {
      console.warn(`[OTA] manifest fetch failed: HTTP ${res.status}`);
      return;
    }
    const m = (typeof res.data === 'string' ? JSON.parse(res.data) : res.data) as Manifest;
    // The plugin hands back its most recent rollback once, then clears it, so
    // persist it here; otherwise the same broken bundle re-downloads every check.
    try {
      const failed = await CapacitorUpdater.getFailedUpdate();
      if (failed?.bundle?.version) rememberFailedVersion(failed.bundle.version);
    } catch (err) {
      console.warn('[OTA] getFailedUpdate unavailable:', err);
    }
    const cur = await CapacitorUpdater.current();
    console.info(
      `[OTA] manifest ${m.version} (minNative ${m.minNativeVersion}); installed bundle ${cur.bundle.version}, native ${cur.native}`
    );
    if (!shouldApplyBundle(m, cur.bundle.version, cur.native, readFailedVersions())) {
      console.info('[OTA] no update applied — already current, failed before, or binary too old for this bundle');
      return;
    }
    console.info(`[OTA] downloading bundle ${m.version}…`);
    const bundle = await CapacitorUpdater.download({ url: m.url, version: m.version, checksum: m.checksum, sessionKey: m.sessionKey });
    await CapacitorUpdater.set(bundle);
    // set() reloads the app, so this line normally never runs.
    console.info(`[OTA] activated bundle ${m.version}`);
  } catch (err) {
    // OTA is best-effort; never disrupt the running app — but do NOT swallow the
    // error silently (project convention: no silent-catch). A failed manifest
    // fetch, signature mismatch, or download error leaves the current bundle
    // untouched; log it so field issues are diagnosable.
    console.warn('[OTA] update check failed; keeping current bundle:', err);
  }
}
